// Mi6 Intelligence Platform — Question-Driven Scoring Engine
// Implements SCORING-MODEL-SPEC.md v3.2.2026-06-10

import bank from "@/data/question-bank.json";

export type ColorBand = "green" | "amber" | "red" | "tbd";

export interface AnswerOption {
  option_id: string;
  letter: string;
  label: string;
  score: number;
  meaning: string;
  is_tbd: boolean;
}

export interface Question {
  question_id: string;
  parent_question_id: string | null;
  trigger_options: string[];
  question_text: string;
  weight_in_dimension: number | null;
  weight_in_sub_block: number | null;
  answer_options: AnswerOption[];
}

export interface Dimension {
  dimension_id: string;
  dimension_name: string;
  short_name: string;
  composite_weight: number;
  questions: Question[];
}

export interface QuestionBank {
  version: string;
  spec_version: string;
  dimensions: Dimension[];
}

export const QBANK = bank as QuestionBank;

// answers map shape:  { [question_id]: option_letter }   ("TBD" if user hit TBD; absent = not yet touched)
export type AnswerMap = Record<string, string>;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

export function getDimension(id: string): Dimension | undefined {
  return QBANK.dimensions.find((d) => d.dimension_id === id);
}

export function parentQuestions(d: Dimension): Question[] {
  return d.questions.filter((q) => q.parent_question_id === null);
}

export function subQuestionsOf(d: Dimension, parentId: string): Question[] {
  return d.questions.filter((q) => q.parent_question_id === parentId);
}

export function findQuestion(d: Dimension, qid: string): Question | undefined {
  return d.questions.find((q) => q.question_id === qid);
}

export function findOption(q: Question, letter: string): AnswerOption | undefined {
  return q.answer_options.find((o) => o.letter === letter);
}

/** Sub-block is active iff parent has a non-TBD answer matching trigger_options */
export function subBlockActive(d: Dimension, parent: Question, answers: AnswerMap): boolean {
  const ans = answers[parent.question_id];
  if (!ans) return false;
  const opt = findOption(parent, ans);
  if (!opt || opt.is_tbd) return false;
  // Sub-questions for this parent define the trigger letters; pull from any sub
  const subs = subQuestionsOf(d, parent.question_id);
  if (subs.length === 0) return false;
  const triggers = subs[0]?.trigger_options ?? [];
  return triggers.includes(ans);
}

/* ------------------------------------------------------------------ */
/* Scoring                                                             */
/* ------------------------------------------------------------------ */

export interface DimensionResult {
  dimension_id: string;
  score: number;            // 0..100, raw (used in composite)
  tbdWeightShare: number;   // 0..1
  answeredCount: number;
  totalParentCount: number;
  percentAnswered: number;  // 0..1, parent-question completion
  color: ColorBand;
  capped: boolean;          // true if cap rule downgraded green -> amber
  tbdDominant: boolean;
}

/**
 * Compute a dimension score, color, and TBD share.
 *
 * Strategy: normalize parent weights to sum to 1.0 (some source files don't
 * cleanly sum). For each parent:
 *   - if a sub-block is active and parent option triggers it, the parent
 *     score contribution uses (1 - sub_weight_fraction) and the sub-block
 *     contributes the rest.
 *   - if parent is TBD, the entire parent weight counts as TBD.
 *   - if parent unanswered, treated as TBD for color purposes (per spec
 *     section 4.6 a final color is gated on every parent having SOME answer;
 *     for live preview we treat unanswered as TBD).
 */
export function scoreDimension(d: Dimension, answers: AnswerMap): DimensionResult {
  const parents = parentQuestions(d);

  // Normalize parent weights to sum to 1
  const rawSum = parents.reduce((s, p) => s + (p.weight_in_dimension ?? 0), 0) || 1;
  const norm = (w: number | null) => (w ?? 0) / rawSum;

  let total = 0;          // sum of contributions (each ~ 0..100 * weight)
  let tbdShare = 0;       // weight share that is TBD or untouched
  let answeredCount = 0;

  for (const p of parents) {
    const w = norm(p.weight_in_dimension);
    const ansLetter = answers[p.question_id];
    const ansOpt = ansLetter ? findOption(p, ansLetter) : undefined;

    if (!ansOpt || ansOpt.is_tbd) {
      // untouched OR explicit TBD — both contribute 0 and count toward tbd share
      tbdShare += w;
      if (ansOpt && ansOpt.is_tbd) answeredCount += 1;
      continue;
    }

    answeredCount += 1;

    // parent answered. check sub-block
    const subs = subQuestionsOf(d, p.question_id);
    const hasActiveSubs = subs.length > 0 && subs[0].trigger_options.includes(ansLetter);

    if (!hasActiveSubs) {
      // parent contributes full weight
      total += ansOpt.score * w;
      continue;
    }

    // sub-question block — fixed sub-weight fraction of 0.5 if not specified
    // (the spec varies per parent; for v1.0 we use a uniform 0.5 split, which
    // matches the worksheet's IS-01 example)
    const subFrac = 0.5;
    total += ansOpt.score * w * (1 - subFrac);

    // sub-block contribution — sum sub option scores weighted by their sub_block weights
    const subRawSum = subs.reduce((s, q) => s + (q.weight_in_sub_block ?? 0), 0) || 1;
    let subTotal = 0;
    let subTbdShare = 0;
    for (const sq of subs) {
      const sw = (sq.weight_in_sub_block ?? 0) / subRawSum;
      const subAnsLetter = answers[sq.question_id];
      const subAnsOpt = subAnsLetter ? findOption(sq, subAnsLetter) : undefined;
      if (!subAnsOpt || subAnsOpt.is_tbd) {
        subTbdShare += sw;
        continue;
      }
      subTotal += subAnsOpt.score * sw;
    }
    // sub block contributes proportionally
    total += subTotal * w * subFrac;
    // record sub-block tbd weight against the dimension's total
    tbdShare += subTbdShare * w * subFrac;
  }

  const score = total; // already on 0..100 scale because weights sum to 1
  const totalParents = parents.length;
  const pctAnswered = totalParents === 0 ? 0 : answeredCount / totalParents;

  // color band
  let color: ColorBand;
  let capped = false;
  let tbdDominant = false;
  if (tbdShare > 0.5) {
    color = "tbd";
    tbdDominant = true;
  } else if (tbdShare > 0.4) {
    if (score >= 75) {
      color = "amber"; // capped
      capped = true;
    } else if (score >= 50) {
      color = "amber";
    } else {
      color = "red";
    }
  } else {
    if (score >= 75) color = "green";
    else if (score >= 50) color = "amber";
    else color = "red";
  }

  return {
    dimension_id: d.dimension_id,
    score: Math.round(score * 10) / 10,
    tbdWeightShare: Math.round(tbdShare * 1000) / 1000,
    answeredCount,
    totalParentCount: totalParents,
    percentAnswered: pctAnswered,
    color,
    capped,
    tbdDominant,
  };
}

export interface CompositeResult {
  score: number;
  color: ColorBand;
  dimensions: DimensionResult[];
}

export function scoreComposite(answers: AnswerMap): CompositeResult {
  const dimResults = QBANK.dimensions.map((d) => scoreDimension(d, answers));
  // Composite uses raw dimension scores (not capped) weighted by composite weight
  const composite = dimResults.reduce((s, r) => {
    const d = QBANK.dimensions.find((x) => x.dimension_id === r.dimension_id)!;
    return s + r.score * d.composite_weight;
  }, 0);
  let color: ColorBand;
  if (composite >= 75) color = "green";
  else if (composite >= 50) color = "amber";
  else color = "red";
  return {
    score: Math.round(composite * 10) / 10,
    color,
    dimensions: dimResults,
  };
}

/* ------------------------------------------------------------------ */
/* Color tokens (Tailwind-friendly)                                    */
/* ------------------------------------------------------------------ */

export const BAND_TOKENS: Record<ColorBand, { bg: string; ring: string; text: string; label: string; dot: string }> = {
  green: {
    bg: "bg-emerald-500/15",
    ring: "ring-emerald-500/40",
    text: "text-emerald-300",
    label: "Green",
    dot: "bg-emerald-400",
  },
  amber: {
    bg: "bg-orange-500/15",
    ring: "ring-orange-500/40",
    text: "text-orange-300",
    label: "Orange",
    dot: "bg-orange-400",
  },
  red: {
    bg: "bg-rose-500/15",
    ring: "ring-rose-500/40",
    text: "text-rose-300",
    label: "Red",
    dot: "bg-rose-400",
  },
  tbd: {
    bg: "bg-neutral-700/60",
    ring: "ring-neutral-500/40",
    text: "text-neutral-300",
    label: "TBD",
    dot: "bg-neutral-400",
  },
};
